import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/contexts/UserContext';
import { toast } from '@/hooks/use-toast';
import { mapSupabaseError } from '@/utils/security';

interface DealApplication {
  id: string;
  deal_id: string;
  talent_profile_id: string;
  status: 'pending' | 'accepted' | 'rejected';
  applied_at: string;
  reviewed_at: string | null;
  notes: string | null;
  initiated_by: 'talent' | 'brand';
}

interface TalentApplicationInfo extends DealApplication {
  talent_name: string;
  talent_photo: string | null;
  talent_sport: string;
  talent_followers: number;
  talent_tier: string;
}

export const useDealApplications = (dealId?: string) => {
  const [applications, setApplications] = useState<TalentApplicationInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { currentUser } = useUser();

  const fetchApplicationsForDeal = async (specificDealId?: string) => {
    const targetDealId = specificDealId || dealId;
    if (!targetDealId) return;

    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('deal_applications')
        .select(`
          *,
        talent:profiles!talent_profile_id (
            id,
            display_name,
            avatar_url
          )
        `)
        .eq('deal_id', targetDealId)
        .order('applied_at', { ascending: false });

      if (fetchError) throw fetchError;

      // Enrich with talent data
      const enrichedApplications = await Promise.all(
        (data || []).map(async (app: any) => {
          const { data: talentData } = await supabase
            .from('talents')
            .select('name, photo, sport, total_followers, tier')
            .eq('profile_id', app.talent_profile_id)
            .maybeSingle();

          let profileName = app.talent?.display_name;
          let profileAvatar = null;
          if (!talentData) {
            const { data: profileData } = await supabase
              .from('profiles')
              .select('display_name, avatar_url')
              .eq('id', app.talent_profile_id)
              .maybeSingle();
            profileName = profileData?.display_name;
            profileAvatar = profileData?.avatar_url;
          }

          return {
            ...app,
            initiated_by: app.initiated_by || 'talent',
            talent_name: talentData?.name || profileName || 'Unknown',
            talent_photo: app.talent?.avatar_url || talentData?.photo || profileAvatar,
            talent_sport: talentData?.sport || 'N/A',
            talent_followers: talentData?.total_followers || 0,
            talent_tier: talentData?.tier || 'Silver',
          };
        })
      );

      setApplications(enrichedApplications);
    } catch (err: any) {
      console.error('Error fetching applications:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getMyApplications = async () => {
    if (!currentUser) return [];

    setLoading(true);
    setError(null);

    try {
      const { data: profileData } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', currentUser.id)
        .single();

      if (!profileData) return [];

      const { data, error: fetchError } = await supabase
        .from('deal_applications')
        .select('*')
        .eq('talent_profile_id', profileData.id);

      if (fetchError) throw fetchError;

      return data || [];
    } catch (err: any) {
      console.error('Error fetching my applications:', err);
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const acceptApplication = async (applicationId: string): Promise<{ success: boolean }> => {
    try {
      // Get the application details
      const { data: application, error: fetchError } = await supabase
        .from('deal_applications')
        .select('deal_id, talent_profile_id')
        .eq('id', applicationId)
        .single();

      if (fetchError) throw fetchError;

      // ─── ORDER MATTERS ───────────────────────────────────────────────
      // Update the deal FIRST so the funder-wallet trigger can reject
      // before we mutate any application rows. If the deal update succeeds
      // we then flip the application(s). If we updated the application
      // first and the deal-update threw, the application would be left in
      // an "accepted" state while the deal stayed available — a confusing
      // partial state for both talent and brand.
      const { error: updateDealError } = await supabase
        .from('deals')
        .update({
          status: 'active',
          talent_profile_id: application.talent_profile_id,
        })
        .eq('id', application.deal_id);

      if (updateDealError) throw updateDealError;

      // Now that the deal is active, mark the winning application accepted.
      const { error: updateAppError } = await supabase
        .from('deal_applications')
        .update({
          status: 'accepted',
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', applicationId);

      if (updateAppError) throw updateAppError;

      // Reject all other pending applications for this deal.
      const { error: rejectOthersError } = await supabase
        .from('deal_applications')
        .update({
          status: 'rejected',
          reviewed_at: new Date().toISOString(),
          notes: 'Deal was awarded to another applicant',
        })
        .eq('deal_id', application.deal_id)
        .eq('status', 'pending')
        .neq('id', applicationId);

      if (rejectOthersError) throw rejectOthersError;

      toast({
        title: 'Application Accepted',
        description: 'The talent has been accepted for this deal.',
      });

      // Refresh applications
      await fetchApplicationsForDeal(application.deal_id);

      return { success: true };
    } catch (err: any) {
      // Wallet-funding guard reject (when activating the deal pushes the
      // brand's commitment above their wallet) bubbles up via Postgres
      // RAISE EXCEPTION; surface that message verbatim.
      toast({
        title: 'Error',
        description: mapSupabaseError(err, 'Failed to accept application.'),
        variant: 'destructive',
      });
      return { success: false };
    }
  };

  const rejectApplication = async (applicationId: string): Promise<{ success: boolean }> => {
    try {
      const { data: application, error: fetchError } = await supabase
        .from('deal_applications')
        .select('deal_id')
        .eq('id', applicationId)
        .single();

      if (fetchError) throw fetchError;

      const { error: updateError } = await supabase
        .from('deal_applications')
        .update({
          status: 'rejected',
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', applicationId);

      if (updateError) throw updateError;

      toast({
        title: 'Application Rejected',
        description: 'The application has been rejected.',
      });

      // Refresh applications
      await fetchApplicationsForDeal(application.deal_id);

      return { success: true };
    } catch (err: any) {
      console.error('Error rejecting application:', err);
      toast({
        title: 'Error',
        description: 'Failed to reject application: ' + err.message,
        variant: 'destructive',
      });
      return { success: false };
    }
  };

  const revokeApplication = async (dealId: string): Promise<{ success: boolean }> => {
    if (!currentUser) {
      return { success: false };
    }

    try {
      // currentUser.id is the profile ID, not the auth user ID
      const profileId = currentUser.id;

      const { data: application, error: fetchError } = await supabase
        .from('deal_applications')
        .select('id')
        .eq('deal_id', dealId)
        .eq('talent_profile_id', profileId)
        .eq('status', 'pending')
        .maybeSingle();

      if (fetchError) throw fetchError;
      if (!application) throw new Error('No pending application found for this deal');

      const { error: deleteError } = await supabase
        .from('deal_applications')
        .delete()
        .eq('id', application.id);

      if (deleteError) throw deleteError;

      toast({
        title: 'Application Revoked',
        description: 'Your application has been withdrawn.',
      });

      return { success: true };
    } catch (err: any) {
      console.error('Error revoking application:', err);
      toast({
        title: 'Error',
        description: 'Failed to revoke application: ' + err.message,
        variant: 'destructive',
      });
      return { success: false };
    }
  };

  const checkMyApplicationStatus = async (dealId: string): Promise<string | null> => {
    if (!currentUser) return null;

    try {
      const { data: profileData } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', currentUser.id)
        .single();

      if (!profileData) return null;

      const { data } = await supabase
        .from('deal_applications')
        .select('status')
        .eq('deal_id', dealId)
        .eq('talent_profile_id', profileData.id)
        .single();

      return data?.status || null;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    if (dealId) {
      fetchApplicationsForDeal();
    }
  }, [dealId]);

  return {
    applications,
    loading,
    error,
    fetchApplicationsForDeal,
    getMyApplications,
    acceptApplication,
    rejectApplication,
    checkMyApplicationStatus,
    revokeApplication,
  };
};
